import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAlerts } from '../context/AlertContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  getBarangayDashboard,
  getIncidents,
  getTasks,
  getDiseaseAlerts,
  getBarangay,
} from '../lib/api';
import {
  Users,
  Stethoscope,
  Pill,
  Ambulance,
  AlertTriangle,
  ClipboardList,
  Package,
  Home,
  Clock,
  CheckCircle,
} from 'lucide-react';

export default function BarangayDashboard() {
  const { user } = useAuth();
  const { setAlerts: setCriticalAlerts } = useAlerts();
  const [dashboard, setDashboard] = useState(null);
  const [barangay, setBarangay] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [user?.barangay_id]);

  // Update critical alerts when incidents or disease alerts change
  useEffect(() => {
    const criticalItems = [];
    
    // Add pending incidents as critical alerts
    incidents.forEach(inc => {
      if (inc.status === 'pending') {
        criticalItems.push({
          id: inc.id,
          type: 'incident',
          message: `${inc.incident_type} emergency: ${inc.location_text || 'Location pending'}`,
          severity: inc.escalated ? 'critical' : 'high',
        });
      }
    });
    
    // Add disease alerts
    alerts.forEach(alert => {
      criticalItems.push({
        id: alert.id,
        type: 'disease',
        message: alert.alert_message,
        severity: 'critical',
      });
    });
    
    setCriticalAlerts(criticalItems);
  }, [incidents, alerts, setCriticalAlerts]);

  const loadData = async () => {
    if (!user?.barangay_id) return;
    
    try {
      const [dashRes, brgyRes, incRes, taskRes, alertRes] = await Promise.all([
        getBarangayDashboard(user.barangay_id),
        getBarangay(user.barangay_id),
        getIncidents({ barangay_id: user.barangay_id, status: 'pending' }),
        getTasks({ barangay_id: user.barangay_id, status: 'pending' }),
        getDiseaseAlerts({ barangay_id: user.barangay_id, acknowledged: false }),
      ]);
      setDashboard(dashRes.data);
      setBarangay(brgyRes.data);
      setIncidents(incRes.data.slice(0, 5));
      setTasks(taskRes.data.slice(0, 5));
      setAlerts(alertRes.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  const statsCards = [
    { icon: Stethoscope, label: 'Total Patients', value: dashboard?.total_patients || 0, color: 'text-blue-600' },
    { icon: Ambulance, label: 'Active Incidents', value: dashboard?.active_incidents || 0, color: 'text-amber-600', alert: dashboard?.active_incidents > 0 },
    { icon: ClipboardList, label: 'Pending Tasks', value: dashboard?.pending_tasks || 0, color: 'text-indigo-600' },
    { icon: Clock, label: 'Overdue Tasks', value: dashboard?.overdue_tasks || 0, color: 'text-red-600', alert: dashboard?.overdue_tasks > 0 },
    { icon: Pill, label: 'Low Stock', value: dashboard?.low_stock_medicines || 0, color: 'text-orange-600', alert: dashboard?.low_stock_medicines > 0 },
    { icon: Package, label: 'Pending Requests', value: dashboard?.pending_requests || 0, color: 'text-purple-600' },
    { icon: AlertTriangle, label: 'Alerts', value: dashboard?.active_alerts || 0, color: 'text-destructive', alert: dashboard?.active_alerts > 0 },
    { icon: Home, label: 'Households', value: dashboard?.households || 0, color: 'text-teal-600' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="barangay-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Public_Sans'] text-foreground">
              {barangay?.name || 'Barangay'} Command Center
            </h1>
            <p className="text-muted-foreground mt-1">
              Welcome back, {user?.full_name}
            </p>
          </div>
          <Badge variant="outline" className="w-fit">
            <Clock className="h-3 w-3 mr-1" />
            Last updated: {new Date().toLocaleTimeString()}
          </Badge>
        </div>

        {/* Alerts Banner */}
        {alerts.length > 0 && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="p-4">
              {alerts.map((alert) => (
                <div key={alert.id} className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  <p className="font-medium text-destructive">{alert.alert_message}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statsCards.map((stat, idx) => (
            <Card
              key={idx}
              className={`${stat.alert ? 'border-amber-300 bg-amber-50/50' : ''}`}
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                  {stat.alert && <span className="h-2 w-2 bg-amber-500 rounded-full animate-pulse" />}
                </div>
                <p className="text-2xl font-bold mt-2">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Active Incidents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ambulance className="h-5 w-5 text-amber-600" />
                Active Emergencies
              </CardTitle>
              <CardDescription>Recent incidents in your barangay</CardDescription>
            </CardHeader>
            <CardContent>
              {incidents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p>No active emergencies</p>
                </div>
              ) : (
                <ScrollArea className="h-[250px]">
                  <div className="space-y-3">
                    {incidents.map((incident) => (
                      <div
                        key={incident.id}
                        className="p-3 border border-slate-200 rounded-sm"
                        data-testid={`incident-${incident.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <Badge
                            variant={
                              incident.incident_type === 'Medical'
                                ? 'default'
                                : incident.incident_type === 'Fire'
                                ? 'destructive'
                                : 'secondary'
                            }
                          >
                            {incident.incident_type}
                          </Badge>
                          <Badge variant="outline">{incident.status}</Badge>
                        </div>
                        <p className="text-sm mt-2 text-muted-foreground">
                          {incident.location_text || 'Location not specified'}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Pending Tasks */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-indigo-600" />
                Pending BHW Tasks
              </CardTitle>
              <CardDescription>Tasks requiring completion</CardDescription>
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p>All tasks completed</p>
                </div>
              ) : (
                <ScrollArea className="h-[250px]">
                  <div className="space-y-3">
                    {tasks.map((task) => (
                      <div
                        key={task.id}
                        className="p-3 border border-slate-200 rounded-sm"
                        data-testid={`task-${task.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="capitalize">
                            {task.task_type.replace('_', ' ')}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        </div>
                        {task.notes && (
                          <p className="text-sm mt-2 text-muted-foreground">{task.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
